import React, { useState, useContext, useEffect } from 'react';
import { Typography, Table, Button, Switch, message } from 'antd';
import Axios from 'axios';
import { apiUrl } from '../utils/config';
import { Context } from '../utils/store';
import { handleTokenExpired } from '../utils/utils';

const { Title } = Typography;

type Room = {
  id: string;
  start: string;
  end: string;
  room_type: string;
  reservation_type: 'instant_reservation' | 'in-day-reservation' | 'not_required';
  descriprion: string;
};

type RoomRow = Room & {
  isDirty: boolean;
};

const RoomManaging: React.FC = () => {
  const { state, dispatch } = useContext(Context);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);


  useEffect(() => {
    fetchRooms();
  }, []);


  const fetchRooms = async () => {
    setLoading(true);
    try {
      const { data } = await Axios.get(
        `${apiUrl}/rooms/all/`,
        { headers: { Authorization: `JWT ${state.token}` } },
      );
      const rooms: Room[] = data?.rooms ?? [];
      setRooms(
        rooms.filter(
          room => room.room_type === "sport" || room.room_type === "study_room"
        ).map(room => ({
        ...room,
        isDirty: false
      })));

    } catch (error) {
      const err = error as any;
      if (
        !handleTokenExpired(err.response?.status, () =>
          dispatch({ type: 'Logout', payload: null })
        )
      ) {
        message.error('Error fetching rooms');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };


  // updates ONE room
  const updateRoom = async (room: Room) => {
    setLoading(true);
    console.log(room);
    try {
      await Axios.put(
        `${apiUrl}/room/update_room/?room_id=${room.id}&start=${room.start}&end=${room.end}&reservation_type=${room.reservation_type}`,
        null,
        { headers: { Authorization: `JWT ${state.token}`, }, }
      );
    } catch (error) {
      const err = error as any;
      if (
        !handleTokenExpired(err.response?.status, () =>
          dispatch({ type: 'Logout', payload: null })
        )
      ) {
        message.error('Error updating room');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }


  // handle switch toggle
  const handleReservationToggle = (roomId: string, checked: boolean) => {
    setRooms(prev =>
      prev.map(room =>
        room.id === roomId
          ? {
            ...room,
            reservation_type: checked ? 'in-day-reservation' : 'not_required',
            isDirty: true
          }
        : room
      )
    );
  };
  

  const handleSave = async () => {
    const dirtyRooms = rooms.filter(i => i.isDirty);

    // save changes
    await Promise.all(
      dirtyRooms.map(room => 
        updateRoom(room as Room)
      )
    );

    // clear dirty tag
    setRooms(prev =>
      prev.map(room =>
        room.isDirty ? {...room, isDirty: true } : room 
      )
    );
  }


  const columns = [
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Start time', dataIndex: 'start', key: 'start' },
    { title: 'End time', dataIndex: 'end', key: 'end' },
    {
      title: 'Enable reserve',
      dataIndex: 'reservation_type',
      key: 'reservation_type',
      render: (reservation_number: string, room: RoomRow) => ( 
        <Switch
          checked={room.reservation_type === 'in-day-reservation'}
          onChange={(checked: boolean) =>
            handleReservationToggle(room.id, checked)
          }
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 16, boxSizing: 'border-box', width: '100%' }} >
      <div style={{
        display: 'flex',         
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      }}>
        <Title level={3}>Room Managing</Title>
        <Button type="primary" disabled={loading} onClick={handleSave} >Save</Button>
      </div>

      <div>
        <Table
          columns={columns}
          dataSource={rooms}
          size='small'
          loading={loading}
        />

      </div>
    </div>
  );

};

export default RoomManaging;
